import React, { useState, useEffect } from "react";
import ControlsHeader from "shared/ControlsHeader";
import { Button, ButtonPurpose, ButtonSize } from "shared/Button";
import TextInput from "shared/@forms/TextInput";
import { ReactComponent as TrashIcon } from "shared/assets/Trash_Btn.svg";
import styles from "./Settings.module.css";
import TrashButtonIcon from "shared/assets/Trash_Btn.svg";
import { fetchDreamcastSettings, updateDreamcastSettings } from "../../api/settings";

export function Settings() {
  // Loading and Error States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reminder Section State
  const [reminderData, setReminderData] = useState([]);
  const [reminderEditMode, setReminderEditMode] = useState(false);
  const [reminderForm, setReminderForm] = useState([]);

  // Default Section State
  const [defaultData, setDefaultData] = useState({
    yearsSinceAccredited: "",
  });
  const [defaultEditMode, setDefaultEditMode] = useState(false);
  const [defaultForm, setDefaultForm] = useState({});

  // Content Specific Section State
  const [contentData, setContentData] = useState([]);
  const [contentEditMode, setContentEditMode] = useState(false);
  const [contentForm, setContentForm] = useState([]);

  // Fetch settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchDreamcastSettings();
        const settings = response.data || response;
        
        // Map reminders data and add IDs if missing
        const reminders = (settings.reminders || []).map((item, index) => ({
          id: item.id || `reminder-${index}`,
          name: item.name || "",
          department: item.department || "",
          email: item.emailAddress || "",
        }));
        
        // Map default data
        const defaultSettings = {
          yearsSinceAccredited: String(settings.default?.yearsSinceAccredited || ""),
        };
        
        // Map content specific data and add IDs if missing
        const contentSpecific = (settings.contentSpecific || []).map((item, index) => ({
          id: item.id || `content-${index}`,
          sku: item.sku || "",
          yearsSinceAccredited: String(item.yearsSinceAccredited || ""),
        }));
        
        setReminderData(reminders);
        setDefaultData(defaultSettings);
        setContentData(contentSpecific);
      } catch (err) {
        setError(err.message || "Failed to load settings");
        console.error("Error loading settings:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Reminder Handlers
  const handleReminderEdit = () => {
    setReminderEditMode(true);
    setReminderForm(reminderData.map(item => ({ ...item })));
  };

  const handleReminderCancel = () => {
    setReminderEditMode(false);
  };

  const handleReminderSave = async () => {
    try {
      setError(null);
      const payloadReminders = reminderForm.map(item => ({
        name: item.name,
        department: item.department,
        emailAddress: item.email,
      }));
      await updateDreamcastSettings({
        reminders: payloadReminders,
        default: {
          yearsSinceAccredited: parseInt(defaultData.yearsSinceAccredited) || 0,
        },
        contentSpecific: contentData.map(item => ({
          sku: item.sku,
          yearsSinceAccredited: parseInt(item.yearsSinceAccredited) || 0,
        })),
      });
      setReminderData(reminderForm.map(item => ({ ...item })));
      setReminderEditMode(false);
    } catch (err) {
      setError(err.message || "Failed to save reminder settings");
      console.error("Error saving reminder settings:", err);
    }
  };

  const handleReminderInputChange = (index, field, value) => {
    const updated = [...reminderForm];
    updated[index][field] = value;
    setReminderForm(updated);
  };

  const handleReminderDelete = (index) => {
    const updated = reminderForm.filter((_, i) => i !== index);
    setReminderForm(updated);
  };

  const handleReminderAddChoice = () => {
    setReminderForm([...reminderForm, { id: Date.now(), name: "", department: "", email: "" }]);
  };

  // Default Handlers
  const handleDefaultEdit = () => {
    setDefaultEditMode(true);
    setDefaultForm({ ...defaultData });
  };

  const handleDefaultCancel = () => {
    setDefaultEditMode(false);
  };

  const handleDefaultSave = async () => {
    try {
      setError(null);
      const payloadReminders = reminderData.map(item => ({
        name: item.name,
        department: item.department,
        emailAddress: item.email,
      }));
      await updateDreamcastSettings({
        reminders: payloadReminders,
        default: {
          yearsSinceAccredited: parseInt(defaultForm.yearsSinceAccredited) || 0,
        },
        contentSpecific: contentData.map(item => ({
          sku: item.sku,
          yearsSinceAccredited: parseInt(item.yearsSinceAccredited) || 0,
        })),
      });
      setDefaultData({ ...defaultForm });
      setDefaultEditMode(false);
    } catch (err) {
      setError(err.message || "Failed to save default settings");
      console.error("Error saving default settings:", err);
    }
  };

  const handleDefaultInputChange = (field, value) => {
    setDefaultForm({ ...defaultForm, [field]: value });
  };

  // Content Specific Handlers
  const handleContentEdit = () => {
    setContentEditMode(true);
    setContentForm(contentData.map(item => ({ ...item })));
  };

  const handleContentCancel = () => {
    setContentEditMode(false);
  };

  const handleContentSave = async () => {
    try {
      setError(null);
      const payloadReminders = reminderData.map(item => ({
        name: item.name,
        department: item.department,
        emailAddress: item.email,
      }));
      await updateDreamcastSettings({
        reminders: payloadReminders,
        default: {
          yearsSinceAccredited: parseInt(defaultData.yearsSinceAccredited) || 0,
        },
        contentSpecific: contentForm.map(item => ({
          sku: item.sku,
          yearsSinceAccredited: parseInt(item.yearsSinceAccredited) || 0,
        })),
      });
      setContentData(contentForm.map(item => ({ ...item })));
      setContentEditMode(false);
    } catch (err) {
      setError(err.message || "Failed to save content settings");
      console.error("Error saving content settings:", err);
    }
  };

  const handleContentInputChange = (index, field, value) => {
    const updated = [...contentForm];
    updated[index][field] = value;
    setContentForm(updated);
  };

  const handleContentDelete = (index) => {
    const updated = contentForm.filter((_, i) => i !== index);
    setContentForm(updated);
  };

  const handleContentAddChoice = () => {
    setContentForm([...contentForm, { id: Date.now(), sku: "", yearsSinceAccredited: "" }]);
  };

 

  if (loading) {
    return (
      <div className={styles.page}>
        <ControlsHeader title="DreamCast - Settings" />
        <div className={styles.topBackground}></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <ControlsHeader title="DreamCast - Settings" />
      <div className={styles.topBackground}></div>
      {error && <div className={styles.errorNotice}>{error}</div>}
      <div className={styles.notifications}>Notifications</div>

      {/* BSM Renewal Reminder Section */}
      <section className={styles.contentCard}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>BSM Renewal Reminder</h2>
          {!reminderEditMode && (
             <div className={styles.actionButtons}>
            <Button
              size={ButtonSize.tiny}
              purpose={ButtonPurpose.generic}
              onClick={handleReminderEdit}
              className={styles.button}
            >
              Edit
            </Button>
            </div>
          )}
          {reminderEditMode && (
            <div className={styles.actionButtons}>
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.cancel}
                onClick={handleReminderCancel}
              >
                Cancel
              </Button>
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.generic}
                onClick={handleReminderSave}
              className={styles.button}

              >
                Save
              </Button>
            </div>
          )}
        </div>

        <div className={styles.sectionContent}>
          {reminderEditMode ? (
            <div className={styles.editForm}>
              {reminderForm.length > 0 ? (
                <>
                  <div className={styles.formRow}>
                    <div className={styles.formField}>
                      <label>Name</label>
                    </div>
                    <div className={styles.formField}>
                      <label>Department</label>
                    </div>
                    <div className={styles.formField}>
                      <label>Email Address</label>
                    </div>
                  </div>
                  {reminderForm.map((item, index) => (
                    <div key={item.id} className={styles.formRow}>
                      <div className={styles.formField}>
                        <TextInput
                          value={item.name}
                          onChange={(e) => handleReminderInputChange(index, "name", e.target.value)}
                          placeholder="N/A"
                        />
                      </div>
                      <div className={styles.formField}>
                        <TextInput
                          value={item.department}
                          onChange={(e) => handleReminderInputChange(index, "department", e.target.value)}
                          placeholder="N/A"
                        />
                      </div>
                      <div className={styles.formField}>
                        <div className={styles.inputWithButton}>
                          <TextInput
                            value={item.email}
                            onChange={(e) => handleReminderInputChange(index, "email", e.target.value)}
                            placeholder="N/A"
                          />
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleReminderDelete(index)}
                            type="button"
                            aria-label="Delete"
                          >
                          <img
                            src={TrashButtonIcon}
                            alt=""
                            className={styles.deleteIcon}
                          />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label>Name</label>
                    <TextInput
                      value=""
                      onChange={() => {}}
                      placeholder="N/A"
                      disabled
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Department</label>
                    <TextInput
                      value=""
                      onChange={() => {}}
                      placeholder="N/A"
                      disabled
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Email Address</label>
                    <TextInput
                      value=""
                      onChange={() => {}}
                      placeholder="N/A"
                      disabled
                    />
                  </div>
                </div>
              )}
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.outline}
                onClick={handleReminderAddChoice}
                className={styles.addChoiceButton}
              >
                Add Choice
              </Button>
            </div>
          ) : (
            <>
              {reminderData.length > 0 ? (
                reminderData.map((item, index) => (
                  <div key={item.id} className={styles.formRow}>
                    <div className={styles.formField}>
                      <label>Name</label>
                      <p>{item.name}</p>
                    </div>
                    <div className={styles.formField}>
                      <label>Department</label>
                      <p>{item.department}</p>
                    </div>
                    <div className={styles.formField}>
                      <label>Email Address</label>
                      <p>{item.email}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label>Name</label>
                    <p>N/A</p>
                  </div>
                  <div className={styles.formField}>
                    <label>Department</label>
                    <p>N/A</p>
                  </div>
                  <div className={styles.formField}>
                    <label>Email Address</label>
                    <p>N/A</p>
                  </div>
                </div>
              )}
            </>
          )}

            <div className={styles.border} />
        </div>
      
   

      {/* Default Section */}
    
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Default</h2>
          {!defaultEditMode && (
            <div className={styles.actionButtons}>
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.generic}
                onClick={handleDefaultEdit}
                className={styles.button}

              >
              Edit
              </Button>
             </div>
          )}
          {defaultEditMode && (
            <div className={styles.actionButtons}>
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.cancel}
                onClick={handleDefaultCancel}
                className={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.generic}
                onClick={handleDefaultSave}
                className={styles.button}

              >
                Save
              </Button>
            </div>
          )}
        </div>

        <div className={styles.sectionContent}>
          {defaultEditMode ? (
            <div className={styles.editForm}>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <label>Years Since Accredited</label>
                  <TextInput
                    value={defaultForm.yearsSinceAccredited}
                    onChange={(e) => handleDefaultInputChange("yearsSinceAccredited", e.target.value)}
                    placeholder="Years Since Accredited"
                  />
                </div>
              </div>
            </div>
          ) : (
           
            <div className={styles.formField}>
              <label>Years Since Accredited</label>
              <p>{defaultData.yearsSinceAccredited}</p>
            </div>
          )}
        </div>
    

      {/* Content Specific Section */}
     
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitleContent}>Content Specific</h2>
          {!contentEditMode && (
            <div className={styles.actionButtons}>
            <Button
              size={ButtonSize.tiny}
              purpose={ButtonPurpose.generic}
              onClick={handleContentEdit}
              className={styles.button}

            >
              Edit
            </Button>
            </div>
          )}
          {contentEditMode && (
            <div className={styles.actionButtons}>
              <Button
                size={ButtonSize.tiny}
                purpose={ButtonPurpose.cancel}
                onClick={handleContentCancel}
              >
                Cancel
              </Button>
              <Button
                size={ButtonSize.small}
                purpose={ButtonPurpose.generic}
                onClick={handleContentSave}
                className={styles.button}

              >
                Save
              </Button>
            </div>
          )}
        </div>

        <div className={styles.sectionContentBottom}>
          {contentEditMode ? (
            <div className={styles.editForm}>
              {contentForm.length > 0 ? (
                <>
                  <div className={styles.formRow}>
                    <div className={styles.formField}>
                      <label>SKU</label>
                    </div>
                    <div className={styles.formField}>
                      <label>Years Since Accredited</label>
                    </div>
                  </div>
                  {contentForm.map((item, index) => (
                    <div key={item.id} className={styles.formRow}>
                      <div className={styles.formField}>
                        <TextInput
                          value={item.sku}
                          onChange={(e) => handleContentInputChange(index, "sku", e.target.value)}
                          placeholder="N/A"
                        />
                      </div>
                      <div className={styles.formField}>
                        <div className={styles.inputWithButton}>
                          <TextInput
                            value={item.yearsSinceAccredited}
                            onChange={(e) => handleContentInputChange(index, "yearsSinceAccredited", e.target.value)}
                            placeholder="N/A"
                          />
                          <button
                          className={styles.deleteButton}
                          onClick={() => handleContentDelete(index)}
                          type="button"
                          aria-label="Delete"
                        >
                        <img
                          src={TrashButtonIcon}
                          alt=""
                          className={styles.deleteIcon}
                        />
                       </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label>SKU</label>
                    <TextInput
                      value=""
                      onChange={() => {}}
                      placeholder="N/A"
                      disabled
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Years Since Accredited</label>
                    <TextInput
                      value=""
                      onChange={() => {}}
                      placeholder="N/A"
                      disabled
                    />
                  </div>
                </div>
              )}
              <Button
                size={ButtonSize.small}
                purpose={ButtonPurpose.outline}
                onClick={handleContentAddChoice}
                className={styles.addButton}

              >
                Add
              </Button>
            </div>
          ) : (
            <>
              {contentData.length > 0 ? (
                contentData.map((item, index) => (
                  <div key={item.id} className={styles.formRow}>
                    <div className={styles.formField}>
                      <label>SKU</label>
                      <p>{item.sku}</p>
                    </div>
                    <div className={styles.formField}>
                      <label>Years Since Accredited</label>
                      <p>{item.yearsSinceAccredited}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label>SKU</label>
                    <p>N/A</p>
                  </div>
                  <div className={styles.formField}>
                    <label>Years Since Accredited</label>
                    <p>N/A</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
  
export default Settings;
